"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import { Search, User, Menu, ChevronDown, ChevronRight, ArrowRight, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";

/**
 * Recursive desktop submenu dropdown renderer for arbitrary depth
 */
function RecursiveDesktopMenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="flex min-w-[200px] flex-col gap-1 p-1 font-sans">
      {items.map((subItem, index) => <li key={subItem.id} data-index={index} className="relative group/sub">
          {subItem.children && subItem.children.length > 0 ? <div className="flex flex-col">
              <Link to={subItem.href || "#"} className="flex items-center justify-between rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-wider text-popover-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                <span>{subItem.label}</span>
                <ChevronRight className="size-3 text-muted-foreground" />
              </Link>
              <div className="pl-3 pt-1 border-l border-border/40 ml-2">
                <RecursiveDesktopMenu items={subItem.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={subItem.href || "#"} className="flex items-center gap-2 rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-wider text-popover-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                <span className="size-1 rounded-full bg-primary/60" />
                <span>{subItem.label}</span>
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

/**
 * Desktop Navigation Menu Consumer
 */
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-sm" />
        <div className="h-8 w-24 bg-muted/40 animate-pulse rounded-sm" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-9 px-3.5 text-xs font-serif font-bold uppercase tracking-wider bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent/60 data-[state=open]:text-accent-foreground transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="border border-border bg-popover text-popover-foreground shadow-2xl p-2 min-w-[220px]">
                  <RecursiveDesktopMenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center justify-center rounded-sm px-3.5 text-xs font-serif font-bold uppercase tracking-wider text-foreground transition-colors hover:text-primary focus:outline-none">
                  <span>{item.label}</span>
                  <span className="absolute bottom-1 left-3.5 right-3.5 h-[2px] scale-x-0 bg-primary transition-transform duration-150 group-hover:scale-x-100" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

/**
 * Recursive mobile menu item list renderer for arbitrary depth
 */
function RecursiveMobileMenu({
  items,
  onNavigate,
  depth = 0
}: {
  items: MenuItem[];
  onNavigate: () => void;
  depth?: number;
}) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleSubmenu = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <ul className={`flex flex-col gap-1 ${depth > 0 ? "pl-3 border-l border-border/40 mt-1" : ""}`}>
      {items.map((item, index) => {
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = Boolean(openItems[item.id]);
      return <li key={item.id} data-index={index} className="flex flex-col">
            <div className="flex items-center justify-between">
              <Link to={item.href || "#"} onClick={onNavigate} className="flex flex-1 items-center gap-2 py-2 text-sm font-serif font-bold uppercase tracking-wider text-foreground hover:text-primary transition-colors">
                {depth > 0 && <span className="size-1 rounded-full bg-primary" />}
                <span>{item.label}</span>
              </Link>
              {hasChildren && <Button type="button" variant="ghost" size="icon" onClick={() => toggleSubmenu(item.id)} aria-label={`Toggle ${item.label} submenu`} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                  <ChevronDown className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </Button>}
            </div>
            {hasChildren && isOpen && item.children && <RecursiveMobileMenu items={item.children} onNavigate={onNavigate} depth={depth + 1} />}
          </li>;
    })}
    </ul>;
}

/**
 * Mobile Drawer Menu Consumer
 */
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 py-4">
        <div className="h-6 w-3/4 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-1/2 bg-muted animate-pulse rounded-sm" />
        <div className="h-6 w-2/3 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <div className="py-2">
      <RecursiveMobileMenu items={menuItems} onNavigate={onNavigate} />
    </div>;
}

/**
 * Search Products Consumer for Popover Dropdown
 */
function SearchProductsConsumer({
  onSelect
}: {
  onSelect?: () => void;
}) {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="space-y-2 py-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex items-center gap-3 p-2 bg-muted/30 rounded-sm animate-pulse">
            <div className="size-10 bg-muted rounded-sm shrink-0" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3 w-3/4 bg-muted rounded-xs" />
              <div className="h-3 w-1/4 bg-muted rounded-xs" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="p-3 text-xs text-destructive border border-destructive/20 bg-destructive/10 rounded-sm">
        Unable to load search results. Please try again.
      </div>;
  }
  if (!products.length) {
    return <div className="py-6 text-center text-xs text-muted-foreground font-mono">
        No matching industrial gear found.
      </div>;
  }
  const visibleProducts = products.slice(0, 6);
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {visibleProducts.map((product, i) => <Link key={product.id} data-index={i} to={product.link || "#"} onClick={onSelect} className="group flex items-center gap-3 p-2 border border-border bg-card hover:border-primary transition-[border-color,background-color] rounded-sm text-left">
          {product.featured_image_url ? <div className="size-11 shrink-0 overflow-hidden rounded-xs bg-muted/60 border border-border/40">
              <img src={product.featured_image_url} alt={product.title} className="size-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
            </div> : <div className="size-11 shrink-0 flex items-center justify-center rounded-xs bg-muted border border-border/40 text-muted-foreground">
              <Wrench className="size-5" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-card-foreground group-hover:text-primary transition-colors">
              {product.title}
            </p>
            <div className="mt-0.5 text-xs font-mono font-bold text-primary">
              <ProductPrice product={product} />
            </div>
          </div>
        </Link>)}
    </div>;
}

/**
 * Product Search Popover Trigger & Results
 */
function HeaderSearchPopover() {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <EcommerceProductsProvider wp_query={wp_query}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Search catalog" className="h-9 w-9 text-foreground hover:text-primary hover:bg-accent transition-colors">
            <Search className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="w-[calc(100vw-2rem)] sm:w-[500px] min-w-[320px] bg-popover border border-border text-popover-foreground shadow-2xl p-4 rounded-sm">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <div className="flex items-center gap-2">
                <span className="h-3 w-[3px] bg-primary" />
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-muted-foreground">
                  Catalog Search
                </span>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">
                PRO GEAR & SPECS
              </span>
            </div>

            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search tools, hardware, equipment..." className="w-full font-sans" />

            <div className="max-h-72 overflow-y-auto pr-1">
              {!hasSearchQuery ? <div className="py-4 text-center">
                  <p className="text-xs text-muted-foreground font-mono">
                    Enter keyword or category to query inventory
                  </p>
                </div> : <div className="space-y-3">
                  <SearchProductsConsumer onSelect={() => setIsOpen(false)} />
                  <div className="border-t border-border/80 pt-2 flex items-center justify-end">
                    <Link to={buildProductSearchUrl(searchValue)} onClick={() => setIsOpen(false)} className="inline-flex items-center gap-1.5 text-xs font-mono font-bold uppercase tracking-wider text-primary hover:underline">
                      <span>View all results</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}

/**
 * Root Header Component
 */
export default function Header() {
  const accountUrl = useWooAccountUrl();
  const prefersReduced = useReducedMotion();
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="dark" className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border text-foreground transition-colors">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <motion.div initial={prefersReduced ? false : {
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: "easeOut"
      }} className="flex h-16 items-center justify-between gap-4">
          {/* Brand Logo Anchor (No duplicate text, Link to "/") */}
          <div className="flex items-center shrink-0">
            <Link to="/" aria-label="Huga Scalet Homepage" className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation Center */}
          <nav aria-label="Main Navigation" className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="22">
              <DesktopNavConsumer />
            </MenuProvider>
          </nav>

          {/* Action Cluster (Right) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Catalog Search Popover */}
            <HeaderSearchPopover />

            {/* Account Link */}
            <Button asChild variant="ghost" size="icon" className="h-9 w-9 text-foreground hover:text-primary hover:bg-accent transition-colors">
              <Link to={accountUrl || "/my-account"} aria-label="My Account">
                <User className="size-4" aria-hidden="true" />
              </Link>
            </Button>

            {/* Shopping Cart Drawer */}
            <CartIcon variant="ghost" size="icon" iconType="cart" cartLayout="drawer" className="h-9 w-9 text-foreground hover:text-primary hover:bg-accent transition-colors" />

            {/* Mobile Sheet Navigation Trigger */}
            <div className="lg:hidden flex items-center">
              <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open mobile menu" className="h-9 w-9 text-foreground hover:text-primary hover:bg-accent transition-colors">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[380px] bg-background text-foreground border-l border-border p-6 flex flex-col justify-between">
                  <div className="space-y-6">
                    <SheetHeader className="p-0 text-left border-b border-border pb-4">
                      <div className="flex items-center gap-2">
                        <span className="h-4 w-[3px] bg-primary" />
                        <SheetTitle className="text-base font-serif font-bold uppercase tracking-wider text-foreground">
                          Navigation Menu
                        </SheetTitle>
                      </div>
                      <SheetDescription className="text-xs font-mono text-muted-foreground">
                        Huga Scalet Industrial Catalog
                      </SheetDescription>
                    </SheetHeader>

                    {/* Mobile Navigation List with Submenus */}
                    <MenuProvider menu_id="22">
                      <MobileNavConsumer onNavigate={() => setMobileSheetOpen(false)} />
                    </MenuProvider>
                  </div>

                  {/* Drawer Footer Actions */}
                  <div className="border-t border-border pt-4 mt-auto space-y-3">
                    <Button asChild variant="outline" className="w-full justify-start gap-2 text-xs font-serif font-bold uppercase tracking-wider border-border hover:border-primary hover:text-primary transition-colors">
                      <Link to={accountUrl || "/my-account"} onClick={() => setMobileSheetOpen(false)}>
                        <User className="size-4 text-primary" />
                        <span>Account Dashboard</span>
                      </Link>
                    </Button>
                    <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground px-1">
                      <span>NATIONWIDE U.S. DELIVERY</span>
                      <span className="size-1.5 rounded-full bg-primary" />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}